import { useCallback, useEffect, useMemo, useState } from "react";
import TenantDocumentListsContext from "./TenantDocumentListsContext";
import {
  getLoadedTenantDocumentListsOrNull,
  subscribeToTenantDocumentLists,
} from "./tenantDocumentListsStore";
import { loadTenantDocumentLists } from "./loadTenantDocumentLists";

// Wraps the whole app (src/index.js, around <App />). Starts loading the
// document lists as soon as the app opens, so they are usually in memory
// before any page that needs them is shown. A page reload loads them again.
const TenantDocumentListsState = ({ children }) => {
  const [state, setState] = useState(() => {
    const loaded = getLoadedTenantDocumentListsOrNull();
    return loaded
      ? { status: "ready", lists: loaded }
      : { status: "loading", lists: null };
  });

  const load = useCallback(() => {
    setState((current) =>
      current.status === "ready" ? current : { status: "loading", lists: null }
    );
    loadTenantDocumentLists().then(
      (lists) => setState({ status: "ready", lists }),
      () =>
        setState((current) =>
          current.status === "ready"
            ? current
            : { status: "error", lists: null }
        )
    );
  }, []);

  useEffect(() => {
    // Also follow a load started elsewhere (e.g. a report page)
    const unsubscribe = subscribeToTenantDocumentLists((lists) =>
      setState({ status: "ready", lists })
    );
    load();
    return unsubscribe;
  }, [load]);

  const value = useMemo(() => ({ ...state, retry: load }), [state, load]);

  return (
    <TenantDocumentListsContext.Provider value={value}>
      {children}
    </TenantDocumentListsContext.Provider>
  );
};

export default TenantDocumentListsState;
